import { createContext, useEffect, useMemo, useState } from 'react'
import { fetchTemplateData } from '../utils/fetchTemplateData'

/**
 * @typedef {object} TemplateDataContextValue
 * @property {object|null} templateData  The full response from exportTemplateData
 * @property {boolean} isLoading
 * @property {Error|null} error
 * @property {boolean} isTemplate  Whether template mode is active
 */

/** @type {React.Context<TemplateDataContextValue>} */
export const TemplateDataContext = createContext({
    templateData: null,
    isLoading: false,
    error: null,
    isTemplate: false,
})

/**
 * Fetches template data from the exportTemplateData cloud function once,
 * then shares it with all child widgets via context.
 *
 * @param {object} props
 * @param {boolean} props.isTemplate  Whether the editor is in template mode
 * @param {React.ReactNode} props.children
 * 
 * @todo use LiveReportCrudContext to determine template mode instead of passing as prop (from URL)
 */
export const TemplateDataProvider = ({ isTemplate, children }) => {

    const [templateData, setTemplateData] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!isTemplate) return

        setIsLoading(true)
        setError(null)

        fetchTemplateData()
            .then(data => {
                setTemplateData(data)
            })
            .catch(err => {
                console.error('TemplateDataProvider fetch error:', err)
                setError(err)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [isTemplate])

    const value = useMemo(
        () => ({ templateData, isLoading, error, isTemplate }),
        [templateData, isLoading, error, isTemplate]
    )

    return (
        <TemplateDataContext.Provider value={value}>
            {children}
        </TemplateDataContext.Provider>
    )
}
